import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import PageShell from "@/components/Layout/PageShell";
import Seo from "@/components/Seo";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useFreeAccessConfig } from "@/lib/freeAccess";

const INTERESTS = [
  "mystery", "fantasy", "anime-inspired", "sports", "sci-fi", "horror",
  "mythology", "romance", "urban stories", "adventure", "history",
  "true stories", "comedy", "gaming", "music", "superheroes",
  "technology", "nature", "survival stories", "biographies",
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const freeCfg = useFreeAccessConfig();
  const [form, setForm] = useState({
    name: "", email: "", password: "", role: "student", grade_band: "middle",
  });
  const [interests, setInterests] = useState([]);
  const [busy, setBusy] = useState(false);

  const toggle = (i) =>
    setInterests((arr) =>
      arr.includes(i) ? arr.filter((x) => x !== i) : arr.length < 5 ? [...arr, i] : arr
    );

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    // Analytics: signup submission attempt (Release 1.2 polish).
    api.post("/analytics/anon", {
      event: "registration_submitted",
      surface: "register",
      metadata: {
        role: form.role,
        attribution: JSON.parse(localStorage.getItem("sr_signup_attribution") || "null"),
      },
    }).catch(() => {});
    try {
      await register({ ...form, interests });
      // Fire free_account_created ONLY after backend confirms creation
      // (register() throws on failure, so we're here only on success).
      const attribution = JSON.parse(
        localStorage.getItem("sr_signup_attribution") || "null",
      );
      api.post("/analytics/anon", {
        event: "free_account_created",
        surface: "register",
        metadata: {
          role: form.role, grade_band: form.grade_band,
          attribution: attribution || undefined,
        },
      }).catch(() => {});
      localStorage.removeItem("sr_signup_attribution");
      toast.success("Account created! Let's read.");
      navigate(loc.state?.from || "/dashboard");
    } catch (err) {
      // Deliberately do NOT fire free_account_created on failure.
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell>
      <Seo
        title="Create a free SparkRead account"
        description="Start free with SparkRead. Pick your interests and grade band, then begin adaptive reading practice with AI coaching."
        path="/register"
      />
      <section className="container-spark py-16 lg:py-20">
        <div className="mx-auto max-w-2xl card-spark p-8" data-testid="register-card">
          <div className="flex flex-col items-center text-center gap-3">
            <Logo variant="auth" as="span" />
            <h1 className="font-display text-xl font-black mb-0">Create your SparkRead account</h1>
          </div>
          <p className="mt-2 text-sm text-muted-foreground text-center">Pick a few interests so we can match passages to what you love.</p>
          <form className="mt-6 grid gap-4" onSubmit={submit}>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold" htmlFor="name">Name</label>
                <input id="name" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="register-name-input"
                  className="mt-1 w-full rounded-2xl border border-input bg-background h-12 px-4 outline-none focus-visible:ring-2 focus-visible:ring-spark-purple" />
              </div>
              <div>
                <label className="text-sm font-semibold" htmlFor="email">Email</label>
                <input id="email" type="email" required value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="register-email-input"
                  className="mt-1 w-full rounded-2xl border border-input bg-background h-12 px-4 outline-none focus-visible:ring-2 focus-visible:ring-spark-purple" />
              </div>
              <div>
                <label className="text-sm font-semibold" htmlFor="password">Password</label>
                <input id="password" type="password" required minLength={6} value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="register-password-input"
                  className="mt-1 w-full rounded-2xl border border-input bg-background h-12 px-4 outline-none focus-visible:ring-2 focus-visible:ring-spark-purple" />
              </div>
              <div>
                <label className="text-sm font-semibold" htmlFor="role">I am a...</label>
                <select id="role" value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  data-testid="register-role-select"
                  className="mt-1 w-full rounded-2xl border border-input bg-background h-12 px-4 outline-none focus-visible:ring-2 focus-visible:ring-spark-purple">
                  <option value="student">Student</option>
                  <option value="teacher">Teacher</option>
                  <option value="parent">Parent</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm font-semibold" htmlFor="grade">Grade band</label>
                <select id="grade" value={form.grade_band}
                  onChange={(e) => setForm({ ...form, grade_band: e.target.value })}
                  data-testid="register-grade-select"
                  className="mt-1 w-full rounded-2xl border border-input bg-background h-12 px-4 outline-none focus-visible:ring-2 focus-visible:ring-spark-purple">
                  <option value="elementary">Elementary</option>
                  <option value="middle">Middle School</option>
                  <option value="high">High School</option>
                  <option value="ap">AP / SAT / ACT</option>
                </select>
              </div>
              {freeCfg?.enabled && form.role === "student" && (
                <p className="sm:col-span-2 rounded-2xl bg-spark-purple/10 p-4 text-sm" data-testid="register-free-access-note">
                  <strong>Free access:</strong> your free account includes {freeCfg.period_days} days or{" "}
                  {freeCfg.limit} completed learning activities (quizzes, timed reads, Daily Spark, vocabulary sets),
                  whichever comes first. The {freeCfg.period_days} days start with your first completed activity.
                  You can upgrade any time; you're never charged automatically.
                </p>
              )}
            </div>

            <div>
              <div className="text-sm font-semibold">Pick 3-5 interests</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {INTERESTS.map((i) => {
                  const active = interests.includes(i);
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => toggle(i)}
                      data-testid={`interest-${i.replace(/\s+/g, "-")}`}
                      className={`pill cursor-pointer transition ${active ? "bg-spark-purple text-white" : ""}`}
                    >
                      {i}
                    </button>
                  );
                })}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">{interests.length}/5 selected</div>
            </div>

            <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="register-submit-btn">
              {busy ? "Creating..." : "Create account"}
            </button>
          </form>
          <div className="mt-4 text-sm text-muted-foreground text-center">
            Have an account? <Link to="/login" className="text-spark-purple font-semibold">Sign in</Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
